import type { Move } from '../../../schemas/move';

/**
 * Archetipi del Bardo (manuale V1, rr. 2325-2335).
 *
 * 5 mosse di tipo `class_special`. Pattern razza-tematico standard, con
 * un'eccezione strutturale notevole:
 *
 * - **Mezz'orco**: la mossa di archetipo richiede al giocatore di **scegliere
 *   una classe** al momento della presa (manuale: "scegli una classe.
 *   Quando prendi una mossa multiclasse, puoi sempre scegliere tra le mosse
 *   di tale classe. In genere i Mezz'orchi scelgono il Barbaro"). La scelta
 *   è modellata via `MoveSchema.choices` (riuso del pattern *Avvelenatore*
 *   del Ladro): 12 opzioni — una per ciascuna delle 12 classi DWR — con
 *   `choiceCount: 1`.
 */

const archetipoUmano: Move = {
  id: 'bardo.archetipo-umano',
  name: 'Archetipo: Umano',
  type: 'class_special',
  classId: 'bardo',
  trigger: 'Quando entri in un insediamento civilizzato',
  description:
    "Sarai sempre accolto da qualcuno che rispetta l'usanza dell'ospitalità per i menestrelli.",
  tags: [],
  prerequisites: [],
  replaces: [],
};

const archetipoElfo: Move = {
  id: 'bardo.archetipo-elfo',
  name: 'Archetipo: Elfo',
  type: 'class_special',
  classId: 'bardo',
  trigger:
    'Quando giungi per la prima volta in un luogo che ritieni importante (a tua discrezione)',
  description: 'Puoi chiedere al GM di raccontarti un fatto della storia che lo riguarda.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const archetipoNano: Move = {
  id: 'bardo.archetipo-nano',
  name: 'Archetipo: Nano',
  type: 'class_special',
  classId: 'bardo',
  trigger: 'Quando usi *Parole di Potere*',
  description: 'Puoi tirare+**COS** al posto di +**CAR**.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const archetipoHalfling: Move = {
  id: 'bardo.archetipo-halfling',
  name: 'Archetipo: Halfling',
  type: 'class_special',
  classId: 'bardo',
  trigger: "Quando usi *Piano d'Azione*",
  description: 'Prendi +1 a qualsiasi tiro fatto per usare le opportunità generate dalla mossa.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const archetipoMezzorco: Move = {
  id: 'bardo.archetipo-mezzorco',
  name: "Archetipo: Mezz'orco",
  type: 'class_special',
  classId: 'bardo',
  trigger: "Alla creazione del personaggio (alla scelta dell'archetipo)",
  description:
    "Scegli una classe (vedi `choices`). Quando prendi una mossa multiclasse, puoi *sempre e comunque* scegliere tra le mosse di tale classe.\n\nIn genere i Mezz'orchi scelgono il Barbaro, ma puoi configurarne una qualsiasi.",
  tags: [],
  prerequisites: [],
  replaces: [],
  // 12 opzioni: una per ciascuna delle 12 classi DWR (vedi ClassIdSchema).
  choices: [
    {
      id: 'barbaro',
      label: 'Barbaro',
      description: "Default suggerito dal manuale per i Mezz'orchi.",
    },
    { id: 'bardo', label: 'Bardo' },
    { id: 'chierico', label: 'Chierico' },
    { id: 'druido', label: 'Druido' },
    { id: 'guerriero', label: 'Guerriero' },
    { id: 'ladro', label: 'Ladro' },
    { id: 'mago', label: 'Mago' },
    { id: 'monaco', label: 'Monaco' },
    { id: 'paladino', label: 'Paladino' },
    { id: 'ranger', label: 'Ranger' },
    { id: 'sciamano', label: 'Sciamano' },
    { id: 'warlock', label: 'Warlock' },
  ],
  choiceCount: 1,
};

export const bardoArchetypeMoves: readonly Move[] = [
  archetipoUmano,
  archetipoElfo,
  archetipoNano,
  archetipoHalfling,
  archetipoMezzorco,
] as const;
